// src/components/common/ChangeAwareInput.tsx
import React from 'react';
import { AlertCircle, ArrowUpRight } from 'lucide-react';

interface ChangeAwareInputProps {
    value: any;
    previousValue?: any;
    label?: string;
    type?: 'text' | 'number' | 'textarea' | 'email' | 'datetime-local';
    name?: string;  // ✅ For error scrolling
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur?: () => void;
    disabled?: boolean;
    readOnly?: boolean;
    showChange?: boolean;
    className?: string;  // ✅ For error styling
    placeholder?: string;
    rows?: number;
    error?: string;  // ✅ For error message
    min?: string | number;
    max?: string | number;
    step?: string | number;
}

const ChangeAwareInput: React.FC<ChangeAwareInputProps> = ({
    value,
    previousValue,
    label,
    type = 'text',
    name,
    onChange,
    onBlur,
    disabled = false,
    readOnly = false,
    showChange = false,
    className = '',
    placeholder,
    rows = 3,
    error,
    min,
    max,
    step,
}) => {
    const hasChanged = showChange && previousValue !== undefined && previousValue !== value;

    const inputProps = {
        name,
        value: value || '',
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            onChange?.(e),
        onBlur,
        disabled,
        readOnly,
        placeholder,
        className: `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${className}`,
        min: min,
        max: max,
        step: step,
    };

    return (
        <div className="space-y-1">
            {label && (
                <label className="block text-sm font-medium text-gray-700">
                    {label}
                    {hasChanged && (
                        <span className="ml-2 text-xs text-blue-600 font-normal">
                            (Changed)
                        </span>
                    )}
                </label>
            )}

            {/* Main Input */}
            {type === 'textarea' ? (
                <textarea
                    {...inputProps}
                    rows={rows}
                />
            ) : (
                <input
                    {...inputProps}
                    type={type}
                />
            )}

            {/* Error message */}
            {error && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                </p>
            )}

            {/* Previous Value Indicator */}
            {hasChanged && (
                <div className="mt-1 p-2 bg-gray-50 rounded-lg border border-gray-200 flex items-start gap-2">
                    <ArrowUpRight className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500">Previous value</p>
                        <p className="text-sm text-gray-600 line-through">
                            {previousValue || '(empty)'}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChangeAwareInput;